/**
 * Admin Finance System - Full Accounting + Tax Prep
 * Queen B Holdings LLC
 * 
 * Sub-sections:
 * 1. Accounting (Expenses, Revenue, Owner Contributions, P&L)
 * 2. Tax Prep (Deductions, Reports, CPA Ready Export)
 * 3. Invoicing & Payments
 * 4. Financial Dashboard
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  DollarSign, TrendingUp, TrendingDown, CreditCard, Receipt, 
  PieChart, Calendar, Filter, Plus, Search, Download, Upload,
  FileText, AlertTriangle, CheckCircle, Clock, Building2,
  ArrowUpRight, ArrowDownRight, ChevronRight, ChevronDown,
  Loader2, X, Edit2, Trash2, Tag, RefreshCw, Calculator,
  Shield, Briefcase, BarChart3, Wallet, AlertCircle, Send,
  FileSpreadsheet, Printer, Users, Home, Landmark, BookOpen
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Service type colors
const SERVICE_COLORS = {
  'Build': '#8B5CF6',
  'Compliance': '#10B981',
  'Dispatch': '#F59E0B',
  'Academy': '#D4AF37',
  'Signature Program': '#EC4899',
  'Consulting': '#6366F1',
  'Other': '#64748B'
};

// Expense category colors
const CATEGORY_COLORS = {
  'Equipment': '#8B5CF6',
  'Software & Subscriptions': '#EC4899',
  'Marketing & Branding': '#F59E0B',
  'Business Operations': '#10B981',
  'Home Office': '#6366F1',
  'Travel & Mileage': '#14B8A6',
  'Education & Courses': '#D4AF37',
  'Professional Services': '#64748B',
  'Dispatch Operations': '#EF4444',
  'Compliance Tools': '#3B82F6',
  'Other': '#9CA3AF'
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = '#D4AF37', onClick }) => (
  <button 
    onClick={onClick}
    className="bg-[#0D0D0D] border border-white/10 p-4 md:p-5 hover:border-white/20 transition-all text-left w-full group"
    data-testid={`metric-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-start justify-between mb-2">
      <div className="p-2 rounded" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      {trend && (
        <div className={`flex items-center gap-1 text-xs ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
          {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {trendValue}
        </div>
      )}
    </div>
    <p className="text-2xl md:text-3xl font-bold text-white mb-1">{value}</p>
    <p className="text-white/50 text-sm">{title}</p>
    {subtitle && <p className="text-white/30 text-xs mt-1">{subtitle}</p>}
  </button>
);

// Main Tab Button
const MainTabButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all whitespace-nowrap ${
      active 
        ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5' 
        : 'border-transparent text-white/50 hover:text-white'
    }`}
  >
    <Icon className="w-4 h-4" />
    <span className="font-medium">{label}</span>
  </button>
);

// Sub Tab Button
const SubTabButton = ({ active, onClick, label, count }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 text-sm transition-all rounded ${
      active 
        ? 'bg-[#D4AF37] text-black font-medium' 
        : 'bg-white/5 text-white/60 hover:bg-white/10'
    }`}
  >
    {label} {count !== undefined && <span className="ml-1 opacity-70">({count})</span>}
  </button>
);

// ============================================
// ADD EXPENSE MODAL
// ============================================
const AddExpenseModal = ({ onClose, onSuccess, categories }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    category: 'Software & Subscriptions',
    vendor: '',
    description: '',
    service_type: 'Admin',
    deductible: true,
    payment_method: 'Credit Card',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.description) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/admin/finance-center/expenses`, {
        ...formData,
        amount: parseFloat(formData.amount),
        receipt_url: receiptFile ? `mock://receipts/${Date.now()}_${receiptFile.name}` : null
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to add expense:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0D0D0D] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-white font-bold text-lg">Add Expense</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-sm mb-1 block">Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-white/50 text-sm mb-1 block">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="0.00"
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
            >
              {categories.map(cat => (
                <option key={cat.name} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Vendor / Source</label>
            <input
              type="text"
              value={formData.vendor}
              onChange={(e) => setFormData({...formData, vendor: e.target.value})}
              placeholder="Company or person you paid"
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="What was this expense for?"
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-sm mb-1 block">Payment Method</label>
              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="Credit Card">Credit Card</option>
                <option value="Debit Card">Debit Card</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cash">Cash</option>
                <option value="Auto-Pay">Auto-Pay</option>
              </select>
            </div>
            <div>
              <label className="text-white/50 text-sm mb-1 block">Service Type</label>
              <select
                value={formData.service_type}
                onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="Admin">Admin</option>
                <option value="Build">Build</option>
                <option value="Compliance">Compliance</option>
                <option value="Dispatch">Dispatch</option>
                <option value="Academy">Academy</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Upload Receipt (PDF/Image)</label>
            <div className="border border-dashed border-white/20 p-4 text-center hover:border-[#D4AF37]/50 transition-colors">
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setReceiptFile(e.target.files[0])}
                className="hidden"
                id="receipt-upload"
              />
              <label htmlFor="receipt-upload" className="cursor-pointer">
                <Upload className="w-6 h-6 mx-auto text-white/40 mb-2" />
                {receiptFile ? (
                  <span className="text-[#D4AF37] text-sm">{receiptFile.name}</span>
                ) : (
                  <span className="text-white/40 text-sm">Click to upload receipt</span>
                )}
              </label>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="deductible"
              checked={formData.deductible}
              onChange={(e) => setFormData({...formData, deductible: e.target.checked})}
              className="w-4 h-4 accent-[#D4AF37]"
            />
            <label htmlFor="deductible" className="text-white/70 text-sm">Tax Deductible</label>
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Notes (optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes..."
              rows={2}
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none resize-none"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading || !formData.amount || !formData.description}
            className="w-full py-3 bg-[#D4AF37] text-black font-bold hover:bg-[#C4A030] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
            Add Expense
          </button>
        </form>
      </div>
    </div>
  );
};

// ============================================
// ADD INCOME MODAL
// ============================================
const AddIncomeModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    service_type: 'Build',
    client: '',
    payment_method: 'Bank Transfer',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/admin/finance-center/income`, {
        ...formData,
        amount: parseFloat(formData.amount),
        category: `${formData.service_type} Services`
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to add income:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0D0D0D] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-white font-bold text-lg">Add Revenue</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-sm mb-1 block">Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-white/50 text-sm mb-1 block">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="0.00"
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Service Type</label>
            <select
              value={formData.service_type}
              onChange={(e) => setFormData({...formData, service_type: e.target.value})}
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="Build">Build (Authority Launch)</option>
              <option value="Compliance">Compliance (Protect)</option>
              <option value="Dispatch">Dispatch (Grow)</option>
              <option value="Academy">Academy (Courses)</option>
              <option value="Signature Program">Signature Program</option>
              <option value="Consulting">Consulting</option>
              <option value="Other">Other</option>
            </select>
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Client Name</label>
            <input
              type="text"
              value={formData.client}
              onChange={(e) => setFormData({...formData, client: e.target.value})}
              placeholder="Client or company name"
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Payment Method</label>
            <select
              value={formData.payment_method}
              onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Credit Card">Credit Card (Stripe)</option>
              <option value="Wire Transfer">Wire Transfer</option>
              <option value="Auto-Pay">Auto-Pay</option>
              <option value="Cash">Cash</option>
              <option value="Check">Check</option>
            </select>
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Notes (optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes..."
              rows={2}
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none resize-none"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading || !formData.amount}
            className="w-full py-3 bg-green-600 text-white font-bold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
            Add Revenue
          </button>
        </form>
      </div>
    </div>
  );
};

// ============================================
// ADD OWNER CONTRIBUTION MODAL
// ============================================
const AddContributionModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    source: 'Personal Funds',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/admin/finance-center/contributions`, {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to add contribution:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0D0D0D] border border-white/10 w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-white font-bold text-lg">Add Owner Contribution</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-4 bg-blue-500/10 border-b border-blue-500/20">
          <p className="text-blue-400 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            Owner contributions are personal investments into your business. They are NOT counted as revenue or taxable income.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-sm mb-1 block">Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-white/50 text-sm mb-1 block">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="0.00"
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Source</label>
            <select
              value={formData.source}
              onChange={(e) => setFormData({...formData, source: e.target.value})}
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
            >
              <option value="Personal Funds">Personal Funds</option>
              <option value="W2 Funding">W2 Paycheck Savings</option>
              <option value="Savings">Personal Savings</option>
              <option value="Credit Line">Personal Credit Line</option>
              <option value="Other">Other</option>
            </select>
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Notes (optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="What was this contribution for?"
              rows={2}
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none resize-none"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading || !formData.amount}
            className="w-full py-3 bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
            Add Contribution
          </button>
        </form>
      </div>
    </div>
  );
};

// ============================================
// ADD INVOICE MODAL
// ============================================
const AddInvoiceModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    invoice_number: `INV-${Date.now().toString().slice(-6)}`,
    client_name: '',
    client_email: '',
    service_type: 'Build',
    amount: '',
    due_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.client_name) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/admin/finance-center/invoices`, {
        ...formData,
        amount: parseFloat(formData.amount),
        status: 'pending',
        items: [{ description: `${formData.service_type} Services`, amount: parseFloat(formData.amount) }]
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to create invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0D0D0D] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-white font-bold text-lg">Create Invoice</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="text-white/50 text-sm mb-1 block">Invoice Number</label>
            <input
              type="text"
              value={formData.invoice_number}
              onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-sm mb-1 block">Client Name</label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                placeholder="Client name"
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="text-white/50 text-sm mb-1 block">Client Email</label>
              <input
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                placeholder="client@email.com"
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-sm mb-1 block">Service Type</label>
              <select
                value={formData.service_type}
                onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
              >
                <option value="Build">Build</option>
                <option value="Compliance">Compliance</option>
                <option value="Dispatch">Dispatch</option>
                <option value="Academy">Academy</option>
                <option value="Signature Program">Signature Program</option>
                <option value="Consulting">Consulting</option>
              </select>
            </div>
            <div>
              <label className="text-white/50 text-sm mb-1 block">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="0.00"
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Due Date</label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({...formData, due_date: e.target.value})}
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          
          <div>
            <label className="text-white/50 text-sm mb-1 block">Notes (optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes..."
              rows={2}
              className="w-full bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none resize-none"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading || !formData.amount || !formData.client_name}
            className="w-full py-3 bg-[#D4AF37] text-black font-bold hover:bg-[#C4A030] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
            Create Invoice
          </button>
        </form>
      </div>
    </div>
  );
};

// ============================================
// MAIN FINANCE SYSTEM COMPONENT
// ============================================
const FinanceSystem = () => {
  // Main section tabs
  const [activeSection, setActiveSection] = useState('dashboard');
  
  // Data states
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [taxReport, setTaxReport] = useState(null);
  const [expenseCategories, setExpenseCategories] = useState([]);
  
  // Modal states
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [showAddContribution, setShowAddContribution] = useState(false);
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  
  // Accounting sub-tab
  const [accountingTab, setAccountingTab] = useState('expenses');
  
  // Tax prep year
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  
  // Fetch data
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/finance-center/dashboard`);
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  }, []);
  
  const fetchExpenses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/finance-center/expenses?limit=100`);
      setExpenses(res.data.expenses || []);
    } catch (err) {
      console.error('Failed to fetch expenses:', err);
    }
  }, []);
  
  const fetchIncome = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/finance-center/income?limit=100`);
      setIncome(res.data.income || []);
    } catch (err) {
      console.error('Failed to fetch income:', err);
    }
  }, []);
  
  const fetchContributions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/finance-center/contributions`);
      setContributions(res.data.contributions || []);
    } catch (err) {
      console.error('Failed to fetch contributions:', err);
    }
  }, []);
  
  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/finance-center/invoices`);
      setInvoices(res.data.invoices || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    }
  }, []);
  
  const fetchTaxReport = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/finance-center/tax-report?year=${taxYear}`);
      setTaxReport(res.data);
    } catch (err) {
      console.error('Failed to fetch tax report:', err);
    }
  }, [taxYear]);
  
  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/finance-center/expense-categories-list`);
      setExpenseCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);
  
  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchDashboard(),
      fetchExpenses(),
      fetchIncome(),
      fetchContributions(),
      fetchInvoices(),
      fetchTaxReport(),
      fetchCategories()
    ]);
    setLoading(false);
  }, [fetchDashboard, fetchExpenses, fetchIncome, fetchContributions, fetchInvoices, fetchTaxReport, fetchCategories]);
  
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);
  
  useEffect(() => {
    if (activeSection === 'tax') {
      fetchTaxReport();
    }
  }, [taxYear, activeSection, fetchTaxReport]);
  
  // Seed demo data
  const seedDemoData = async () => {
    try {
      await axios.post(`${API}/api/admin/finance-center/seed-demo`);
      fetchAll();
    } catch (err) {
      console.error('Failed to seed demo data:', err);
    }
  };
  
  // Delete handlers
  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/api/admin/finance-center/expenses/${id}`);
      fetchExpenses();
      fetchDashboard();
    } catch (err) {
      console.error('Failed to delete expense:', err);
    }
  };
  
  const deleteIncome = async (id) => {
    if (!window.confirm('Delete this income entry?')) return;
    try {
      await axios.delete(`${API}/api/admin/finance-center/income/${id}`);
      fetchIncome();
      fetchDashboard();
    } catch (err) {
      console.error('Failed to delete income:', err);
    }
  };
  
  const deleteContribution = async (id) => {
    if (!window.confirm('Delete this contribution?')) return;
    try {
      await axios.delete(`${API}/api/admin/finance-center/contributions/${id}`);
      fetchContributions();
      fetchDashboard();
    } catch (err) {
      console.error('Failed to delete contribution:', err);
    }
  };
  
  const markInvoicePaid = async (id) => {
    try {
      await axios.put(`${API}/api/admin/finance-center/invoices/${id}/mark-paid`);
      fetchInvoices();
      fetchDashboard();
    } catch (err) {
      console.error('Failed to mark invoice paid:', err);
    }
  };
  
  // Export helpers
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(item => Object.values(item).map(v => `"${v}"`).join(',')).join('\n');
    const csv = `${headers}\n${rows}`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Wallet className="w-7 h-7 text-[#D4AF37]" />
                Finance System
              </h1>
              <p className="text-white/50 text-sm">Queen B Holdings LLC • Full Accounting & Tax Prep</p>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={seedDemoData}
                className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Load Demo Data
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Tabs */}
      <div className="border-b border-white/10 bg-[#0A0A0A] overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <MainTabButton 
              active={activeSection === 'dashboard'} 
              onClick={() => setActiveSection('dashboard')} 
              icon={BarChart3} 
              label="Dashboard" 
            />
            <MainTabButton 
              active={activeSection === 'accounting'} 
              onClick={() => setActiveSection('accounting')} 
              icon={Calculator} 
              label="Accounting" 
            />
            <MainTabButton 
              active={activeSection === 'tax'} 
              onClick={() => setActiveSection('tax')} 
              icon={FileSpreadsheet} 
              label="Tax Prep" 
            />
            <MainTabButton 
              active={activeSection === 'invoicing'} 
              onClick={() => setActiveSection('invoicing')} 
              icon={FileText} 
              label="Invoicing" 
            />
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        
        {/* ============================================ */}
        {/* DASHBOARD SECTION */}
        {/* ============================================ */}
        {activeSection === 'dashboard' && dashboard && (
          <div className="space-y-6">
            {/* Metric Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                title="Monthly Revenue"
                value={formatCurrency(dashboard.current_month?.revenue)}
                subtitle={dashboard.current_month?.name}
                icon={TrendingUp}
                color="#10B981"
                trend={dashboard.trends?.revenue_change > 0 ? 'up' : 'down'}
                trendValue={`${Math.abs(dashboard.trends?.revenue_change || 0).toFixed(1)}%`}
              />
              <MetricCard
                title="Monthly Expenses"
                value={formatCurrency(dashboard.current_month?.expenses)}
                subtitle={dashboard.current_month?.name}
                icon={TrendingDown}
                color="#EF4444"
                trend={dashboard.trends?.expense_change > 0 ? 'up' : 'down'}
                trendValue={`${Math.abs(dashboard.trends?.expense_change || 0).toFixed(1)}%`}
              />
              <MetricCard
                title="Net Profit"
                value={formatCurrency(dashboard.current_month?.profit)}
                subtitle={`${dashboard.current_month?.profit_margin}% margin`}
                icon={DollarSign}
                color={dashboard.current_month?.profit >= 0 ? '#D4AF37' : '#EF4444'}
              />
              <MetricCard
                title="Owner Investment"
                value={formatCurrency(dashboard.owner_contributions)}
                subtitle="Total contributions"
                icon={Landmark}
                color="#6366F1"
              />
            </div>
            
            {/* YTD Summary */}
            <div className="bg-[#0D0D0D] border border-white/10 p-6">
              <h3 className="text-lg font-bold text-white mb-4">Year-to-Date Summary</h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-white/50 text-sm">YTD Revenue</p>
                  <p className="text-2xl font-bold text-green-400">{formatCurrency(dashboard.ytd?.revenue)}</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm">YTD Expenses</p>
                  <p className="text-2xl font-bold text-red-400">{formatCurrency(dashboard.ytd?.expenses)}</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm">YTD Profit</p>
                  <p className={`text-2xl font-bold ${dashboard.ytd?.profit >= 0 ? 'text-[#D4AF37]' : 'text-red-400'}`}>
                    {formatCurrency(dashboard.ytd?.profit)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Charts Row */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Revenue by Service */}
              <div className="bg-[#0D0D0D] border border-white/10 p-6">
                <h3 className="text-lg font-bold text-white mb-4">Revenue by Service</h3>
                <div className="space-y-3">
                  {dashboard.revenue_by_service?.map((item, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-white/70">{item.service}</span>
                        <span className="text-white">{formatCurrency(item.amount)}</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full"
                          style={{ 
                            width: `${(item.amount / (dashboard.current_month?.revenue || 1)) * 100}%`,
                            backgroundColor: SERVICE_COLORS[item.service] || '#D4AF37'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Top Expenses */}
              <div className="bg-[#0D0D0D] border border-white/10 p-6">
                <h3 className="text-lg font-bold text-white mb-4">Top Expense Categories</h3>
                <div className="space-y-3">
                  {dashboard.top_expense_categories?.map((item, idx) => (
                    <div key={idx}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-white/70">{item.category}</span>
                        <span className="text-white">{formatCurrency(item.amount)}</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full"
                          style={{ 
                            width: `${(item.amount / (dashboard.current_month?.expenses || 1)) * 100}%`,
                            backgroundColor: CATEGORY_COLORS[item.category] || '#D4AF37'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Pending Invoices Alert */}
            {dashboard.pending_invoices?.count > 0 && (
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-white font-medium">
                      {dashboard.pending_invoices.count} Pending Invoice{dashboard.pending_invoices.count > 1 ? 's' : ''}
                    </p>
                    <p className="text-white/50 text-sm">
                      {formatCurrency(dashboard.pending_invoices.amount)} awaiting payment
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setActiveSection('invoicing')}
                  className="px-4 py-2 bg-[#D4AF37] text-black font-medium text-sm"
                >
                  View Invoices
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* ============================================ */}
        {/* ACCOUNTING SECTION */}
        {/* ============================================ */}
        {activeSection === 'accounting' && (
          <div className="space-y-6">
            {/* Sub Tabs */}
            <div className="flex flex-wrap gap-2">
              <SubTabButton active={accountingTab === 'expenses'} onClick={() => setAccountingTab('expenses')} label="Expenses" count={expenses.length} />
              <SubTabButton active={accountingTab === 'revenue'} onClick={() => setAccountingTab('revenue')} label="Revenue" count={income.length} />
              <SubTabButton active={accountingTab === 'contributions'} onClick={() => setAccountingTab('contributions')} label="Owner Contributions" count={contributions.length} />
              <SubTabButton active={accountingTab === 'pnl'} onClick={() => setAccountingTab('pnl')} label="Profit & Loss" />
            </div>
            
            {/* Expenses Tab */}
            {accountingTab === 'expenses' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-white">Expense Tracking</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => exportToCSV(expenses, 'expenses')}
                      className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Export
                    </button>
                    <button
                      onClick={() => setShowAddExpense(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-medium text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Expense
                    </button>
                  </div>
                </div>
                
                {/* Expenses Table */}
                <div className="bg-[#0D0D0D] border border-white/10 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10 bg-white/5">
                          <th className="text-left text-white/50 text-xs uppercase p-3">Date</th>
                          <th className="text-left text-white/50 text-xs uppercase p-3">Category</th>
                          <th className="text-left text-white/50 text-xs uppercase p-3">Vendor</th>
                          <th className="text-left text-white/50 text-xs uppercase p-3">Description</th>
                          <th className="text-right text-white/50 text-xs uppercase p-3">Amount</th>
                          <th className="text-center text-white/50 text-xs uppercase p-3">Deductible</th>
                          <th className="text-center text-white/50 text-xs uppercase p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expenses.map((expense) => (
                          <tr key={expense.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="p-3 text-white/70 text-sm">{formatDate(expense.date)}</td>
                            <td className="p-3">
                              <span 
                                className="px-2 py-1 text-xs rounded"
                                style={{ 
                                  backgroundColor: `${CATEGORY_COLORS[expense.category] || '#64748B'}20`,
                                  color: CATEGORY_COLORS[expense.category] || '#64748B'
                                }}
                              >
                                {expense.category}
                              </span>
                            </td>
                            <td className="p-3 text-white/70 text-sm">{expense.vendor || '-'}</td>
                            <td className="p-3 text-white text-sm">{expense.description}</td>
                            <td className="p-3 text-red-400 text-sm text-right font-medium">-{formatCurrency(expense.amount)}</td>
                            <td className="p-3 text-center">
                              {expense.deductible ? (
                                <CheckCircle className="w-4 h-4 text-green-400 mx-auto" />
                              ) : (
                                <X className="w-4 h-4 text-white/30 mx-auto" />
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <button 
                                onClick={() => deleteExpense(expense.id)}
                                className="text-white/30 hover:text-red-400"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {expenses.length === 0 && (
                          <tr>
                            <td colSpan={7} className="p-8 text-center text-white/30">No expenses recorded</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            
            {/* Revenue Tab */}
            {accountingTab === 'revenue' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-white">Revenue Tracking</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => exportToCSV(income, 'revenue')}
                      className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Export
                    </button>
                    <button
                      onClick={() => setShowAddIncome(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Revenue
                    </button>
                  </div>
                </div>
                
                {/* Income Table */}
                <div className="bg-[#0D0D0D] border border-white/10 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10 bg-white/5">
                          <th className="text-left text-white/50 text-xs uppercase p-3">Date</th>
                          <th className="text-left text-white/50 text-xs uppercase p-3">Service</th>
                          <th className="text-left text-white/50 text-xs uppercase p-3">Client</th>
                          <th className="text-left text-white/50 text-xs uppercase p-3">Payment</th>
                          <th className="text-right text-white/50 text-xs uppercase p-3">Amount</th>
                          <th className="text-center text-white/50 text-xs uppercase p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {income.map((item) => (
                          <tr key={item.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="p-3 text-white/70 text-sm">{formatDate(item.date)}</td>
                            <td className="p-3">
                              <span 
                                className="px-2 py-1 text-xs rounded"
                                style={{ 
                                  backgroundColor: `${SERVICE_COLORS[item.service_type] || '#64748B'}20`,
                                  color: SERVICE_COLORS[item.service_type] || '#64748B'
                                }}
                              >
                                {item.service_type}
                              </span>
                            </td>
                            <td className="p-3 text-white text-sm">{item.client || '-'}</td>
                            <td className="p-3 text-white/50 text-sm">{item.payment_method}</td>
                            <td className="p-3 text-green-400 text-sm text-right font-medium">+{formatCurrency(item.amount)}</td>
                            <td className="p-3 text-center">
                              <button 
                                onClick={() => deleteIncome(item.id)}
                                className="text-white/30 hover:text-red-400"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {income.length === 0 && (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-white/30">No revenue recorded</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            
            {/* Owner Contributions Tab */}
            {accountingTab === 'contributions' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-bold text-white">Owner Contributions</h3>
                    <p className="text-white/50 text-sm">Personal investments into your business (NOT revenue)</p>
                  </div>
                  <button
                    onClick={() => setShowAddContribution(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Contribution
                  </button>
                </div>
                
                {/* Summary Card */}
                <div className="bg-blue-500/10 border border-blue-500/30 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Landmark className="w-8 h-8 text-blue-400" />
                      <div>
                        <p className="text-white/50 text-sm">Total Owner Investment</p>
                        <p className="text-2xl font-bold text-blue-400">
                          {formatCurrency(contributions.reduce((sum, c) => sum + c.amount, 0))}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Contributions Table */}
                <div className="bg-[#0D0D0D] border border-white/10 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10 bg-white/5">
                          <th className="text-left text-white/50 text-xs uppercase p-3">Date</th>
                          <th className="text-left text-white/50 text-xs uppercase p-3">Source</th>
                          <th className="text-left text-white/50 text-xs uppercase p-3">Notes</th>
                          <th className="text-right text-white/50 text-xs uppercase p-3">Amount</th>
                          <th className="text-center text-white/50 text-xs uppercase p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contributions.map((item) => (
                          <tr key={item.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="p-3 text-white/70 text-sm">{formatDate(item.date)}</td>
                            <td className="p-3">
                              <span className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-400">
                                {item.source}
                              </span>
                            </td>
                            <td className="p-3 text-white/70 text-sm">{item.notes || '-'}</td>
                            <td className="p-3 text-blue-400 text-sm text-right font-medium">{formatCurrency(item.amount)}</td>
                            <td className="p-3 text-center">
                              <button 
                                onClick={() => deleteContribution(item.id)}
                                className="text-white/30 hover:text-red-400"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {contributions.length === 0 && (
                          <tr>
                            <td colSpan={5} className="p-8 text-center text-white/30">No contributions recorded</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            
            {/* P&L Tab */}
            {accountingTab === 'pnl' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-white">Profit & Loss Statement</h3>
                
                {/* Monthly P&L */}
                <div className="bg-[#0D0D0D] border border-white/10 p-6">
                  <h4 className="text-white font-medium mb-4">{dashboard?.current_month?.name || 'Current Month'}</h4>
                  
                  <div className="space-y-4">
                    {/* Revenue */}
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-white">Total Revenue</span>
                      <span className="text-green-400 font-bold">{formatCurrency(dashboard?.current_month?.revenue)}</span>
                    </div>
                    
                    {/* Expenses */}
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-white">Total Expenses</span>
                      <span className="text-red-400 font-bold">({formatCurrency(dashboard?.current_month?.expenses)})</span>
                    </div>
                    
                    {/* Net Profit */}
                    <div className="flex justify-between items-center py-3 bg-[#D4AF37]/10 px-3 -mx-3">
                      <span className="text-white font-bold">Net Profit</span>
                      <span className={`text-xl font-bold ${(dashboard?.current_month?.profit || 0) >= 0 ? 'text-[#D4AF37]' : 'text-red-400'}`}>
                        {formatCurrency(dashboard?.current_month?.profit)}
                      </span>
                    </div>
                    
                    {/* Margin */}
                    <div className="flex justify-between items-center py-2 text-white/50">
                      <span>Profit Margin</span>
                      <span>{dashboard?.current_month?.profit_margin}%</span>
                    </div>
                  </div>
                </div>
                
                {/* YTD P&L */}
                <div className="bg-[#0D0D0D] border border-white/10 p-6">
                  <h4 className="text-white font-medium mb-4">Year-to-Date</h4>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-white">Total Revenue</span>
                      <span className="text-green-400 font-bold">{formatCurrency(dashboard?.ytd?.revenue)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-white">Total Expenses</span>
                      <span className="text-red-400 font-bold">({formatCurrency(dashboard?.ytd?.expenses)})</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-3 bg-[#D4AF37]/10 px-3 -mx-3">
                      <span className="text-white font-bold">YTD Net Profit</span>
                      <span className={`text-xl font-bold ${(dashboard?.ytd?.profit || 0) >= 0 ? 'text-[#D4AF37]' : 'text-red-400'}`}>
                        {formatCurrency(dashboard?.ytd?.profit)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* ============================================ */}
        {/* TAX PREP SECTION */}
        {/* ============================================ */}
        {activeSection === 'tax' && taxReport && (
          <div className="space-y-6">
            {/* Year Selector */}
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-white">Tax Preparation</h3>
                <p className="text-white/50 text-sm">CPA-ready reports and deduction summaries</p>
              </div>
              <div className="flex items-center gap-4">
                <select
                  value={taxYear}
                  onChange={(e) => setTaxYear(parseInt(e.target.value))}
                  className="bg-white/5 border border-white/10 px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                >
                  {[2026, 2025, 2024].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium text-sm"
                >
                  <Printer className="w-4 h-4" />
                  CPA Ready Report
                </button>
              </div>
            </div>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                title="Gross Revenue"
                value={formatCurrency(taxReport.summary?.total_revenue)}
                icon={TrendingUp}
                color="#10B981"
              />
              <MetricCard
                title="Total Deductions"
                value={formatCurrency(taxReport.summary?.total_deductible)}
                icon={Receipt}
                color="#F59E0B"
              />
              <MetricCard
                title="Net Profit"
                value={formatCurrency(taxReport.summary?.net_profit)}
                icon={DollarSign}
                color="#D4AF37"
              />
              <MetricCard
                title="Est. Tax Liability"
                value={formatCurrency(taxReport.tax_estimates?.total_tax_estimate)}
                subtitle={`${taxReport.tax_estimates?.effective_rate}% effective rate`}
                icon={Calculator}
                color="#EF4444"
              />
            </div>
            
            {/* Tax Estimates */}
            <div className="bg-[#0D0D0D] border border-white/10 p-6">
              <h4 className="text-white font-medium mb-4">Tax Estimates Breakdown</h4>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white/5 p-4 rounded">
                  <p className="text-white/50 text-sm">Self-Employment Tax (15.3%)</p>
                  <p className="text-xl font-bold text-white">{formatCurrency(taxReport.tax_estimates?.self_employment_tax)}</p>
                </div>
                <div className="bg-white/5 p-4 rounded">
                  <p className="text-white/50 text-sm">Estimated Income Tax</p>
                  <p className="text-xl font-bold text-white">{formatCurrency(taxReport.tax_estimates?.income_tax_estimate)}</p>
                </div>
                <div className="bg-[#D4AF37]/10 p-4 rounded border border-[#D4AF37]/30">
                  <p className="text-[#D4AF37] text-sm">Recommended Quarterly Payment</p>
                  <p className="text-xl font-bold text-[#D4AF37]">{formatCurrency(taxReport.tax_estimates?.recommended_quarterly_payment)}</p>
                </div>
              </div>
            </div>
            
            {/* Deductions by Category */}
            <div className="bg-[#0D0D0D] border border-white/10 p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-white font-medium">Deductions by Category</h4>
                <button
                  onClick={() => exportToCSV(taxReport.deductions_by_category, 'tax_deductions')}
                  className="flex items-center gap-2 px-3 py-1 text-xs bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
                >
                  <Download className="w-3 h-3" />
                  Export CSV
                </button>
              </div>
              <div className="space-y-3">
                {taxReport.deductions_by_category?.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <span className="text-white">{item.category}</span>
                      <span className="text-white/30 text-xs">({item.transactions} transactions)</span>
                    </div>
                    <span className="text-white font-medium">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Quarterly Breakdown */}
            <div className="bg-[#0D0D0D] border border-white/10 p-6">
              <h4 className="text-white font-medium mb-4">Quarterly Breakdown</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {taxReport.quarterly_breakdown?.map((q) => (
                  <div key={q.quarter} className="bg-white/5 p-4 rounded text-center">
                    <p className="text-white/50 text-sm mb-2">{q.name}</p>
                    <p className="text-green-400 text-sm">+{formatCurrency(q.revenue)}</p>
                    <p className="text-red-400 text-sm">-{formatCurrency(q.expenses)}</p>
                    <p className={`font-bold mt-1 ${q.profit >= 0 ? 'text-white' : 'text-red-400'}`}>
                      {formatCurrency(q.profit)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Owner Contributions Note */}
            <div className="bg-blue-500/10 border border-blue-500/30 p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-400 font-medium">Owner Contributions: {formatCurrency(taxReport.summary?.owner_contributions)}</p>
                <p className="text-white/50 text-sm">
                  Owner contributions are capital investments and are NOT included in taxable revenue.
                  Keep documentation for your CPA.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* ============================================ */}
        {/* INVOICING SECTION */}
        {/* ============================================ */}
        {activeSection === 'invoicing' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-white">Invoicing & Payments</h3>
                <p className="text-white/50 text-sm">Track invoices and payment status</p>
              </div>
              <button
                onClick={() => setShowAddInvoice(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium text-sm"
              >
                <Plus className="w-4 h-4" />
                Create Invoice
              </button>
            </div>
            
            {/* Invoice Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#0D0D0D] border border-white/10 p-4">
                <p className="text-white/50 text-sm">Paid</p>
                <p className="text-2xl font-bold text-green-400">
                  {formatCurrency(invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0))}
                </p>
                <p className="text-white/30 text-xs">{invoices.filter(i => i.status === 'paid').length} invoices</p>
              </div>
              <div className="bg-[#0D0D0D] border border-white/10 p-4">
                <p className="text-white/50 text-sm">Pending</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {formatCurrency(invoices.filter(i => i.status === 'pending').reduce((sum, i) => sum + i.amount, 0))}
                </p>
                <p className="text-white/30 text-xs">{invoices.filter(i => i.status === 'pending').length} invoices</p>
              </div>
              <div className="bg-[#0D0D0D] border border-white/10 p-4">
                <p className="text-white/50 text-sm">Overdue</p>
                <p className="text-2xl font-bold text-red-400">
                  {formatCurrency(invoices.filter(i => i.status === 'overdue').reduce((sum, i) => sum + i.amount, 0))}
                </p>
                <p className="text-white/30 text-xs">{invoices.filter(i => i.status === 'overdue').length} invoices</p>
              </div>
            </div>
            
            {/* Invoices Table */}
            <div className="bg-[#0D0D0D] border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="text-left text-white/50 text-xs uppercase p-3">Invoice #</th>
                      <th className="text-left text-white/50 text-xs uppercase p-3">Client</th>
                      <th className="text-left text-white/50 text-xs uppercase p-3">Service</th>
                      <th className="text-left text-white/50 text-xs uppercase p-3">Due Date</th>
                      <th className="text-right text-white/50 text-xs uppercase p-3">Amount</th>
                      <th className="text-center text-white/50 text-xs uppercase p-3">Status</th>
                      <th className="text-center text-white/50 text-xs uppercase p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3 text-white font-mono text-sm">{invoice.invoice_number}</td>
                        <td className="p-3">
                          <p className="text-white text-sm">{invoice.client_name}</p>
                          {invoice.client_email && (
                            <p className="text-white/40 text-xs">{invoice.client_email}</p>
                          )}
                        </td>
                        <td className="p-3">
                          <span 
                            className="px-2 py-1 text-xs rounded"
                            style={{ 
                              backgroundColor: `${SERVICE_COLORS[invoice.service_type] || '#64748B'}20`,
                              color: SERVICE_COLORS[invoice.service_type] || '#64748B'
                            }}
                          >
                            {invoice.service_type}
                          </span>
                        </td>
                        <td className="p-3 text-white/70 text-sm">{formatDate(invoice.due_date)}</td>
                        <td className="p-3 text-white text-sm text-right font-medium">{formatCurrency(invoice.amount)}</td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 text-xs rounded ${
                            invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                            invoice.status === 'overdue' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {invoice.status !== 'paid' && (
                              <button 
                                onClick={() => markInvoicePaid(invoice.id)}
                                className="text-green-400 hover:text-green-300 text-xs flex items-center gap-1"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Mark Paid
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {invoices.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-white/30">No invoices yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Stripe Integration Notice */}
            <div className="bg-white/5 border border-white/10 p-4 flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-white/40 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white/70 font-medium">Stripe Integration Ready</p>
                <p className="text-white/40 text-sm">
                  Connect your Stripe account to automatically track payments and send invoices.
                  Currently using manual payment tracking.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Modals */}
      {showAddExpense && (
        <AddExpenseModal 
          onClose={() => setShowAddExpense(false)} 
          onSuccess={() => { fetchExpenses(); fetchDashboard(); }}
          categories={expenseCategories}
        />
      )}
      {showAddIncome && (
        <AddIncomeModal 
          onClose={() => setShowAddIncome(false)} 
          onSuccess={() => { fetchIncome(); fetchDashboard(); }}
        />
      )}
      {showAddContribution && (
        <AddContributionModal 
          onClose={() => setShowAddContribution(false)} 
          onSuccess={() => { fetchContributions(); fetchDashboard(); }}
        />
      )}
      {showAddInvoice && (
        <AddInvoiceModal 
          onClose={() => setShowAddInvoice(false)} 
          onSuccess={() => { fetchInvoices(); fetchDashboard(); }}
        />
      )}
    </div>
  );
};

export default FinanceSystem;
